import { z } from 'zod';
import { generateStructuredObject } from './structured.js';
import { validatePrompt } from './input-validation.js';

// Prompt refinement (single / merge / initial). Resolves to the payload shape
// the retired server 'refine:completed' message carried.

const FALLBACK_THEMES = [
  'dramatic chiaroscuro lighting and bold contrast',
  'lush neon color palette with futuristic accents',
  'soft cinematic atmosphere with volumetric light',
  'graphic composition with strong geometric shapes',
  'dreamlike haze with ethereal highlights',
  'high-energy motion blur and dynamic perspective'
];

function normalizePromptEntry(entry) {
  if (typeof entry === 'string') {
    return entry.trim();
  }
  if (entry && typeof entry === 'object' && typeof entry.prompt === 'string') {
    return entry.prompt.trim();
  }
  return '';
}

function deriveBasePrompt({ mode, prompt, selectedPrompts }) {
  if (typeof prompt === 'string' && prompt.trim()) {
    return prompt.trim();
  }

  if (mode !== 'initial' && Array.isArray(selectedPrompts) && selectedPrompts.length > 0) {
    const first = selectedPrompts.find(p => normalizePromptEntry(p));
    if (first) {
      return normalizePromptEntry(first);
    }
  }

  return '';
}

function ensureVariantQuota({ object, mode, prompt, selectedPrompts }) {
  const desiredCount = 4;
  const existing = Array.isArray(object.variants) ? object.variants : [];
  const variants = [];
  const seen = new Set();

  existing.forEach((variant) => {
    if (typeof variant !== 'string') return;
    const trimmed = variant.trim();
    if (!trimmed) return;
    const signature = trimmed.toLowerCase();
    if (seen.has(signature)) return;
    seen.add(signature);
    variants.push(trimmed);
  });

  if (variants.length >= desiredCount) {
    return variants.slice(0, desiredCount);
  }

  const basePrompt = deriveBasePrompt({ mode, prompt, selectedPrompts });
  let themeIndex = 0;

  while (variants.length < desiredCount && themeIndex < FALLBACK_THEMES.length * 3) {
    const theme = FALLBACK_THEMES[themeIndex % FALLBACK_THEMES.length];
    const focusLabel = themeIndex < FALLBACK_THEMES.length
      ? `Focus ${variants.length + 1}: ${theme}`
      : `Alternate focus ${variants.length + 1}: ${theme}`;

    const candidate = basePrompt
      ? `${basePrompt}\n\n${focusLabel}.`
      : `Creative reinterpretation highlighting ${theme}.`;

    const signature = candidate.toLowerCase();
    if (!seen.has(signature)) {
      variants.push(candidate);
      seen.add(signature);
    }

    themeIndex += 1;
  }

  return variants.slice(0, desiredCount);
}

const flattenToolJson = (baseSchema) => {
  const isRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);

  const toolEntrySchema = z.object({
    name: z.string().optional(),
    arguments: z.unknown().optional(),
    function_call: z
      .object({
        name: z.string().optional(),
        arguments: z.unknown().optional()
      })
      .optional()
  }).passthrough();

  const normalizePayload = (payload, entryName) => {
    if (payload == null) {
      return {};
    }

    if (typeof payload === 'string') {
      if (entryName === 'notes') {
        return { notes: payload };
      }
      return { variants: [payload] };
    }

    if (Array.isArray(payload)) {
      return {
        variants: payload.filter((item) => typeof item === 'string' && item.trim().length > 0)
      };
    }

    if (!isRecord(payload)) {
      return {};
    }

    const variants = [];

    if (Array.isArray(payload.variants)) {
      variants.push(
        ...payload.variants.filter((item) => typeof item === 'string' && item.trim().length > 0)
      );
    }

    if (typeof payload.variant === 'string' && payload.variant.trim().length > 0) {
      variants.push(payload.variant.trim());
    }

    if (Array.isArray(payload.prompts)) {
      variants.push(
        ...payload.prompts.filter((item) => typeof item === 'string' && item.trim().length > 0)
      );
    }

    if (typeof payload.prompt === 'string' && payload.prompt.trim().length > 0) {
      variants.push(payload.prompt.trim());
    }

    const noteCandidate =
      typeof payload.notes === 'string'
        ? payload.notes
        : typeof payload.note === 'string'
          ? payload.note
          : typeof payload.text === 'string'
            ? payload.text
            : null;

    const normalized = {
      variants,
      notes: noteCandidate ? noteCandidate.trim() : undefined
    };

    if (typeof payload.merged === 'string' && payload.merged.trim().length > 0) {
      normalized.merged = payload.merged.trim();
    }

    return normalized;
  };

  const collectFromEntry = (entry) => {
    if (!isRecord(entry)) {
      return normalizePayload(entry);
    }

    if ('variants' in entry || 'notes' in entry || 'merged' in entry) {
      return normalizePayload(entry);
    }

    const toolName = typeof entry.name === 'string' ? entry.name.toLowerCase() : undefined;
    const directArguments = entry.arguments;
    const functionArguments = entry.function_call?.arguments;

    if (directArguments !== undefined) {
      return normalizePayload(directArguments, toolName);
    }

    if (functionArguments !== undefined) {
      const functionName = entry.function_call?.name
        ? entry.function_call.name.toLowerCase()
        : toolName;
      return normalizePayload(functionArguments, functionName);
    }

    return normalizePayload(entry, toolName);
  };

  const flattenEntries = (input) => {
    if (input == null) {
      return [];
    }

    const queue = Array.isArray(input) ? [...input] : [input];
    const flattened = [];

    while (queue.length > 0) {
      const current = queue.shift();
      if (current == null) {
        continue;
      }
      if (Array.isArray(current)) {
        queue.push(...current);
        continue;
      }
      flattened.push(current);
    }

    return flattened;
  };

  const aggregateEntries = (entries) => {
    const flattenedEntries = flattenEntries(entries);
    const accumulator = {
      variants: [],
      notes: [],
      merged: undefined
    };

    flattenedEntries.forEach((entry) => {
      const normalized = collectFromEntry(entry);
      if (!normalized) {
        return;
      }

      if (Array.isArray(normalized.variants)) {
        accumulator.variants.push(
          ...normalized.variants.filter((item) => typeof item === 'string' && item.trim().length > 0)
        );
      }

      if (typeof normalized.notes === 'string' && normalized.notes.trim().length > 0) {
        accumulator.notes.push(normalized.notes.trim());
      }

      if (
        typeof normalized.merged === 'string' &&
        normalized.merged.trim().length > 0 &&
        !accumulator.merged
      ) {
        accumulator.merged = normalized.merged.trim();
      }
    });

    const variants = accumulator.variants.map((item) => item.trim()).filter((item) => item.length > 0);
    const notes = accumulator.notes.map((item) => item.trim()).filter((item) => item.length > 0);

    const result = {
      ...(accumulator.merged ? { merged: accumulator.merged } : {}),
      variants,
      notes: notes.length > 0 ? notes.join('\n') : null
    };

    if (!result.variants.length) {
      for (const entry of flattenedEntries) {
        const candidate = collectFromEntry(entry);
        if (!candidate) {
          continue;
        }
        const parsed = baseSchema.safeParse(candidate);
        if (parsed.success) {
          return parsed.data;
        }
      }
    }

    return result;
  };

  const recursiveSchema = z.lazy(() =>
    z.union([baseSchema, toolEntrySchema, z.array(recursiveSchema).min(1)])
  );

  return recursiveSchema.transform((value) => {
    if (Array.isArray(value)) {
      return aggregateEntries(value);
    }

    const parsedBase = baseSchema.safeParse(value);
    if (parsedBase.success) {
      return parsedBase.data;
    }

    return aggregateEntries([value]);
  });
};

export async function refinePrompts(data) {
  const {
    prompt,
    selectedPrompts,
    batchWeights,
    mode,
    promptStylePreference,
    variantCount = 4
  } = data;

  if (prompt) {
    validatePrompt(prompt);
  }
  if (selectedPrompts && Array.isArray(selectedPrompts)) {
    for (const p of selectedPrompts) {
      if (typeof p === 'string') {
        validatePrompt(p);
      } else if (p && typeof p.prompt === 'string') {
        validatePrompt(p.prompt);
      }
    }
  }

  // Clamp variantCount to 1-8
  const clampedVariantCount = Math.min(8, Math.max(1, Math.round(variantCount)));
  console.log('[Refine] Received variantCount:', variantCount, '→ clamped:', clampedVariantCount);

  const stylePreference = typeof promptStylePreference === 'string'
    ? promptStylePreference.trim()
    : '';

  try {
    console.log(`🔄 Refining prompts (mode: ${mode}, selected: ${Array.isArray(selectedPrompts) ? selectedPrompts.length : 0})`);


    let schema, systemPrompt, userPrompt;

    if (mode === 'single') {
      // Single image refinement - create N variations
      schema = z.object({
        variants: z.array(z.string()).length(clampedVariantCount).describe(`${clampedVariantCount} creative variations of the prompt`),
        notes: z.string().nullish().describe('Brief notes about the refinements')
      });

      systemPrompt = `You are an expert at refining AI image generation prompts for the Flux.1 model.
Key requirements:
- Create diverse, creative variations that explore different aspects
- Each variant should be distinct but related to the original
- Flux does NOT support negative prompting - use only positive descriptions
- Keep prompts concise and focused (100-150 words each, MAX 200 words)
- Focus on visual elements: style, mood, lighting, composition, colors
- IMPORTANT: Return EXACTLY ${clampedVariantCount} prompt variants in the variants array, no more, no less`;

      userPrompt = `Create ${clampedVariantCount} creative variations of this prompt for Flux.1 model:

Original: "${prompt}"

Make each variation explore a different creative direction while maintaining the core concept.
Return your response with exactly ${clampedVariantCount} variants in the variants array. Any additional notes should go in the separate notes field.`;

    } else if (mode === 'merge') {
      // Multi-image merge with batch weighting
      schema = z.object({
        merged: z.string().describe('The synthesized concept combining all selected elements'),
        variants: z.array(z.string()).length(clampedVariantCount).describe(`${clampedVariantCount} variations of the merged concept`),
        notes: z.string().nullish().describe('Brief notes about the merge')
      });

      systemPrompt = `You are an expert at synthesizing multiple image concepts for the Flux.1 model.
Key requirements:
- Intelligently combine the best elements from all selected concepts
- Pay special attention to concepts marked with PRIORITY (they should influence the merge more)
- Create a harmonious blend that preserves the essence of each input
- Flux does NOT support negative prompting - use only positive descriptions
- Keep prompts concise and focused (100-150 words each, MAX 200 words)
- Generate diverse variations that explore the merged concept from different angles
- IMPORTANT: Return EXACTLY ${clampedVariantCount} prompt variants in the variants array, no more, no less`;

      // Apply batch weights to prompts
      const weightedPrompts = selectedPrompts.map((p, i) => {
        const weight = batchWeights[p.batchId] || 1;
        if (weight > 1) {
          return `[PRIORITY ${weight}x WEIGHT] ${p.prompt || p}`;
        }
        return p.prompt || p;
      });

      userPrompt = `Synthesize these image concepts into one unified vision:

${weightedPrompts.map((p, i) => `${i + 1}. ${p}`).join('\n')}

Create a merged prompt that harmoniously combines these concepts, giving more weight to PRIORITY items.
Then generate ${clampedVariantCount} creative variations of this merged concept.
Return your response with exactly ${clampedVariantCount} variants in the variants array. Any additional notes should go in the separate notes field.`;

    } else if (mode === 'initial') {
      // Initial generation from wizard - create N diverse starting points
      schema = z.object({
        variants: z.array(z.string()).length(clampedVariantCount).describe(`${clampedVariantCount} diverse interpretations of the brief`),
        notes: z.string().nullish().describe('Brief notes about the interpretations')
      });

      systemPrompt = `You are an expert at creating diverse initial image generation prompts from creative briefs.
Key requirements:
- Create ${clampedVariantCount} distinctly different interpretations of the brief
- Each should explore a different artistic style, mood, or perspective
- Flux does NOT support negative prompting - use only positive descriptions
- Keep prompts concise and focused (100-150 words each, MAX 200 words)
- Make them diverse enough to give the user interesting choices
- Include rich visual details: lighting, colors, composition, atmosphere
- IMPORTANT: Return EXACTLY ${clampedVariantCount} prompt variants in the variants array, no more, no less`;

      userPrompt = `Create ${clampedVariantCount} diverse initial image prompts from this creative brief:

"${prompt}"

Make each prompt explore a completely different artistic interpretation.
Return your response with exactly ${clampedVariantCount} variants in the variants array. Any additional notes should go in the separate notes field.`;
    }

    if (stylePreference) {
      const styleDirective = `\n\nSTYLE PRIORITY: The user has a strong stylistic preference:\n${stylePreference}\nEnsure every prompt strongly reflects this preference.`;
      systemPrompt += styleDirective;
      userPrompt += `\n\nStyle preference to emphasize: ${stylePreference}`;
    }

    console.log('🔵 Refine request:', mode);
    const startTime = Date.now();

    // Base schema (unwrapped)
    const baseSchema = mode === 'merge'
      ? z.object({
          merged: z.string(),
          variants: z.array(z.string()).describe(`${clampedVariantCount} variations of the merged concept`),
          notes: z.string().nullish()
        })
      : z.object({
          variants: z.array(z.string()).describe(`${clampedVariantCount} creative variations`),
          notes: z.string().nullish()
        });

    // Lenient schema accepts tool-style payload wrappers around the base shape
    const lenientSchema = flattenToolJson(baseSchema);

    const object = await generateStructuredObject({
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      schema: lenientSchema,
      baseSchema: baseSchema,
      temperature: 0.8,
      maxTokens: 4500
    });

    const responseTime = Date.now() - startTime;
    console.log(`✅ Generated ${object.variants?.length || 0} variants in ${responseTime}ms`);

    // Validate and fix variants array if needed
    if (!object.variants || object.variants.length !== clampedVariantCount) {
      console.warn(
        '⚠️ LLM returned incorrect number of variants:',
        object.variants?.length,
        'expected:',
        clampedVariantCount,
        'Fixing...'
      );

      if (object.variants && object.variants.length > clampedVariantCount) {
        console.log('Trimming variants array from', object.variants.length, 'to', clampedVariantCount, 'elements');
        object.variants = object.variants.slice(0, clampedVariantCount);
      } else {
        const beforeCount = Array.isArray(object.variants) ? object.variants.length : 0;
        object.variants = ensureVariantQuota({ object, mode, prompt, selectedPrompts });
        // If fallback still returns wrong count, trim/pad to match
        if (object.variants.length > clampedVariantCount) {
          object.variants = object.variants.slice(0, clampedVariantCount);
        } else if (object.variants.length < clampedVariantCount) {
          // Pad with duplicates if needed
          while (object.variants.length < clampedVariantCount && object.variants.length > 0) {
            object.variants.push(object.variants[object.variants.length % object.variants.length]);
          }
        }
        console.log(`🛠️ Fallback variants applied (${beforeCount} → ${object.variants.length})`);
      }
    }

    return {
      type: 'refine:completed',
      result: object
    };

  } catch (error) {
    console.error('Refine error:', error);

    if (error.message && error.message.includes('JSON')) {
      throw new Error('Sorry, please try again later.');
    }
    throw error;
  }
}
